import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Profile } from '@/lib/types';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useState } from 'react';

const editUserSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email: z.string().email().optional().or(z.literal('')),
  job_title: z.string().optional().or(z.literal('')),
  department: z.string().optional().or(z.literal('')),
  work_email: z.string().email('Invalid email').optional().or(z.literal('')),
  work_phone: z.string().optional().or(z.literal('')),
  contact_number: z.string().optional().or(z.literal('')),
  office_location: z.string().optional().or(z.literal('')),
  bio: z.string().max(500).optional().or(z.literal('')),
  email_signature: z.string().optional().or(z.literal('')),
});

type EditUserFormValues = z.infer<typeof editUserSchema>;

interface EditUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: Profile | null;
  onUserUpdated: (user: Profile) => void;
}

export const EditUserDialog: React.FC<EditUserDialogProps> = ({ open, onOpenChange, user, onUserUpdated }) => {
  const { toast } = useToast();
  const form = useForm<EditUserFormValues>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      first_name: '', last_name: '', email: '', job_title: '', department: '',
      work_email: '', work_phone: '', contact_number: '', office_location: '', bio: '', email_signature: '',
    },
  });

  useEffect(() => {
    if (user) {
      form.reset({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        job_title: user.job_title || '',
        department: user.department || '',
        work_email: user.work_email || '',
        work_phone: user.work_phone || '',
        contact_number: (user as any).contact_number || '',
        office_location: user.office_location || '',
        bio: user.bio || '',
        email_signature: (user as any).email_signature || '',
      });
    }
  }, [user, form]);

  const onSubmit = async (data: EditUserFormValues) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: data.first_name,
          last_name: data.last_name,
          job_title: data.job_title || null,
          department: data.department || null,
          work_email: data.work_email || null,
          work_phone: data.work_phone || null,
          contact_number: data.contact_number || null,
          office_location: data.office_location || null,
           bio: data.bio || null,
          email_signature: data.email_signature || null,
        } as any)
        .eq('id', user.id);

      if (error) throw error;

      toast({ title: 'User updated', description: `${data.first_name} ${data.last_name} has been updated.` });
      onUserUpdated({ ...user, ...data } as any);
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: 'Update failed', description: err.message, variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>Update user profile information.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="first_name" render={({ field }) => (
                <FormItem><FormLabel>First Name</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
              )} />
              <FormField control={form.control} name="last_name" render={({ field }) => (
                <FormItem><FormLabel>Last Name</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
              )} />
            </div>
            <FormField control={form.control} name="email" render={({ field }) => (
              <FormItem><FormLabel>Email</FormLabel><FormControl><Input {...field} disabled className="bg-muted/50" /></FormControl><FormMessage /></FormItem>
            )} />
            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="job_title" render={({ field }) => (
                <FormItem><FormLabel>Job Title</FormLabel><FormControl><Input placeholder="e.g. Sales Manager" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
              <FormField control={form.control} name="department" render={({ field }) => (
                <FormItem><FormLabel>Department</FormLabel><FormControl><Input placeholder="e.g. Sales" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="work_email" render={({ field }) => (
                <FormItem><FormLabel>Work Email</FormLabel><FormControl><Input type="email" placeholder="work@company.com" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
              <FormField control={form.control} name="work_phone" render={({ field }) => (
                <FormItem><FormLabel>Work Phone</FormLabel><FormControl><Input placeholder="+1 555 123 4567" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
            </div>
            <FormField control={form.control} name="contact_number" render={({ field }) => (
              <FormItem><FormLabel>Contact Number</FormLabel><FormControl><Input placeholder="+1 555 987 6543" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="office_location" render={({ field }) => (
              <FormItem><FormLabel>Office Location</FormLabel><FormControl><Input placeholder="e.g. Dublin Office" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="bio" render={({ field }) => (
              <FormItem><FormLabel>Bio</FormLabel><FormControl><Textarea placeholder="Short bio..." className="min-h-[80px]" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="email_signature" render={({ field }) => (
              <FormItem>
                <FormLabel>Email Signature (HTML)</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Paste your HTML email signature here..."
                    className="min-h-[120px] font-mono text-xs"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
                {field.value && (
                  <div className="mt-2 border rounded-md p-4 bg-white">
                    <p className="text-xs text-muted-foreground mb-2 font-medium">Preview:</p>
                    <div dangerouslySetInnerHTML={{ __html: field.value }} />
                  </div>
                )}
              </FormItem>
            )} />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
